import { NewsFeedItem } from "@/shared/api/firehose/models";
import { createWordBoundaryRegex } from "@/utils/text-utils";
import {
  FirehoseFilterMatcher,
  FirehosePortfolioMatcher,
} from "@/types/Matchers";
import { Portfolio } from "@/types/Portfolio";
import {
  extractKeywordsFromPortfolios,
  extractTickersFromPortfolios,
} from "../shared/hooks/searchUtils";
import { CategoryQuery } from "@/widget-components/newsquawk-headlines/models/CategoryQuery";

export const firehosePortfolioMatcher: FirehosePortfolioMatcher = (
  item: NewsFeedItem,
  portfolios: Portfolio[]
) => {
  // Check tickers (case-sensitive)
  if (portfolios.length > 0) {
    const tickers = extractTickersFromPortfolios(portfolios);

    // Only test if there are actually tickers to match
    if (tickers.length > 0) {
      const tickerRegex = createWordBoundaryRegex(tickers, false); // case-sensitive

      if (
        tickerRegex.test(item.body || "") ||
        tickerRegex.test(item.title) ||
        tickerRegex.test(item.source) ||
        tickerRegex.test(item.source_attributable || "")
      ) {
        return true;
      }

      // Only process story tickers if text fields didn't match
      if (hasTickers(item)) {
        const storyTickers = normalizeTickers(item.tickers);
        // Test each ticker individually to avoid string concatenation
        if (storyTickers.some((ticker) => tickerRegex.test(ticker))) {
          return true;
        }
      }
    }
  }

  // Check keywords (case-insensitive)
  const keywords = extractKeywordsFromPortfolios(portfolios);

  if (keywords.length > 0) {
    const keywordRegex = createWordBoundaryRegex(keywords, true); // case-insensitive

    if (
      keywordRegex.test(item.body || "") ||
      keywordRegex.test(item.title) ||
      keywordRegex.test(item.source) ||
      keywordRegex.test(item.source_attributable || "")
    ) {
      return true;
    }
  }

  return false;
};

export function hasTickers(it: NewsFeedItem) {
  return "tickers" in it;
}

export function normalizeTickers(raw: unknown): string[] {
  if (!raw) return [];
  const parts = Array.isArray(raw)
    ? raw.flatMap((v) => String(v).split(/[;,]+/))
    : typeof raw === "string"
      ? raw.split(/[;,]+/)
      : [];
  // trim, filter empties, dedupe (case-insensitive), uppercase
  const seen = new Set<string>();

  const cleaned = [];
  for (const p of parts.map((s) => s.trim()).filter(Boolean)) {
    const up = p.toUpperCase();
    if (!seen.has(up)) {
      seen.add(up);
      cleaned.push(up);
    }
  }
  return cleaned;
}

// Checks if firehose item matches the provided filters using the specified logic
// Logic: (ANY keyword) [keywordOp] (ANY ticker)
export const firehoseFilterMatcher: FirehoseFilterMatcher = (
  item: NewsFeedItem,
  filter: CategoryQuery
): boolean => {
  const portfolios = filter.portfolios || [];

  const tickers = extractTickersFromPortfolios(portfolios);
  const searchKeyword = filter.searchKeyword ? [filter.searchKeyword] : [];
  const watchlistKeywords = extractKeywordsFromPortfolios(portfolios);

  const keywords = Array.from(
    new Set([...searchKeyword, ...watchlistKeywords])
  );

  const hasKeywords = keywords.length > 0;
  const hasTickers = tickers.length > 0;

  // If no filters provided, match everything
  if (!hasKeywords && !hasTickers) {
    return true;
  }

  // Check if ANY keyword matches
  let keywordMatches = false;
  if (hasKeywords) {
    const keywordRegex = createWordBoundaryRegex(keywords);
    keywordMatches =
      keywordRegex.test(item.title) ||
      keywordRegex.test(item.body || "") ||
      keywordRegex.test(item.source) ||
      keywordRegex.test(item.source_attributable || "");
  }

  // Check if ANY ticker matches
  let tickerMatches = false;
  if (hasTickers) {
    tickerMatches = firehosePortfolioMatcher(item, portfolios);
  }

  // Edge case: Only keywords provided
  if (hasKeywords && !hasTickers) {
    return keywordMatches;
  }

  // Edge case: Only tickers provided
  if (!hasKeywords && hasTickers) {
    return tickerMatches;
  }

  // Both keywords and tickers provided: use keywordOp (default OR to match backend)
  const keywordOp = filter.keywordOp || "OR";
  if (keywordOp === "OR") {
    return keywordMatches || tickerMatches;
  } else {
    return keywordMatches && tickerMatches;
  }
};
